/**
 * Users Hooks
 *
 * TanStack Query-backed hooks for organization members and roles. Reads share
 * the `['organization', ...]` cache; the role mutation invalidates the list
 * and the affected member's detail entry. Auth-gated to mirror `useDomains`.
 */

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useAuth } from '../auth/auth0-provider';
import {
  getOrganizationMembers,
  getMemberDetail,
  updateMemberRole,
  getAvailableRoles,
  OrganizationMember,
  MemberDetail,
  AvailableRole,
  UpdateRoleResponse,
} from '@/api/usersApi';
import { DEFAULT_PER_PAGE, type Paginated, type PaginationParams } from '@/api/pagination';

/**
 * Hook for fetching organization members (paginated).
 *
 * Backed by `['organization', 'members', { page, perPage }]`.
 */
export function useOrganizationMembers(params: PaginationParams = {}) {
  const { isAuthenticated, isSdkReady } = useAuth();
  const page = params.page ?? 1;
  const perPage = params.perPage ?? DEFAULT_PER_PAGE;

  return useQuery<Paginated<OrganizationMember>>({
    queryKey: ['organization', 'members', { page, perPage }],
    queryFn: () => getOrganizationMembers({ page, perPage }),
    enabled: isAuthenticated && isSdkReady,
    placeholderData: keepPreviousData,
  });
}

/**
 * Hook for fetching a single member's details.
 *
 * Backed by `['organization', 'members', userId]`.
 */
export function useMember(userId: string | undefined) {
  const { isAuthenticated, isSdkReady } = useAuth();

  return useQuery<MemberDetail>({
    queryKey: ['organization', 'members', userId],
    queryFn: () => getMemberDetail(userId!),
    enabled: !!userId && isAuthenticated && isSdkReady,
  });
}

/**
 * Hook for fetching the assignable roles for the organization.
 *
 * Backed by `['organization', 'roles']`.
 */
export function useRoles() {
  const { isAuthenticated, isSdkReady } = useAuth();

  return useQuery<AvailableRole[]>({
    queryKey: ['organization', 'roles'],
    queryFn: () => getAvailableRoles(),
    enabled: isAuthenticated && isSdkReady,
  });
}

/**
 * Hook for updating a member's role.
 *
 * Invalidates the members list and the affected member's detail entry on
 * success.
 */
export function useUpdateMemberRole() {
  const queryClient = useQueryClient();
  return useMutation<UpdateRoleResponse, Error, { userId: string; role: string }>({
    mutationFn: ({ userId, role }) => updateMemberRole(userId, role),
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: ['organization', 'members'] });
      queryClient.invalidateQueries({ queryKey: ['organization', 'members', userId] });
    },
  });
}
